import { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Trash2,
  Plus,
  Save,
  Building,
  User,
  PenLine,
  Calendar,
  MapPin,
  FileText,
  GripVertical,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELD_TYPES = [
  { value: 'vendor_legal_name', label: 'Vendor Legal Name', role: 'vendor', icon: Building },
  { value: 'vendor_address', label: 'Vendor Address', role: 'vendor', icon: MapPin },
  { value: 'vendor_signing_authority_name', label: 'Vendor Signing Authority Name', role: 'vendor', icon: User },
  { value: 'vendor_signing_authority_position', label: 'Vendor Signing Authority Position', role: 'vendor', icon: User },
  { value: 'vendor_signature', label: 'Vendor Signature', role: 'vendor', icon: PenLine },
  { value: 'vendor_signature_date', label: 'Vendor Signature Date', role: 'vendor', icon: Calendar },
  { value: 'company_signatory_name', label: 'Company Signatory Name', role: 'company', icon: User },
  { value: 'company_signatory_title', label: 'Company Signatory Title', role: 'company', icon: User },
  { value: 'company_signature', label: 'Company Signature', role: 'company', icon: PenLine },
  { value: 'company_signature_date', label: 'Company Signature Date', role: 'company', icon: Calendar },
  { value: 'custom_text', label: 'Custom Text Field', role: 'vendor', icon: FileText },
];

const FieldBadge = ({ field, isSelected, onSelect, onDelete, onDragStart }) => {
  const fieldType = FIELD_TYPES.find(f => f.value === field.field_type);
  const Icon = fieldType?.icon || FileText;
  const isVendor = field.role === 'vendor';
  
  return (
    <div
      className={`absolute cursor-move border-2 rounded px-2 py-1 text-xs flex items-center gap-1 transition-all ${
        isSelected 
          ? 'border-blue-500 bg-blue-500/20 ring-2 ring-blue-500/50' 
          : isVendor 
            ? 'border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20' 
            : 'border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20'
      }`}
      style={{
        left: field.position_x,
        top: field.position_y,
        width: field.width,
        height: field.height,
        zIndex: isSelected ? 100 : 10,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(field);
      }}
      draggable
      onDragStart={(e) => onDragStart(e, field)}
      data-testid={`field-${field.id}`}
    >
      <GripVertical className="w-3 h-3 text-zinc-400" />
      <Icon className={`w-3 h-3 ${isVendor ? 'text-amber-400' : 'text-emerald-400'}`} />
      <span className={`truncate ${isVendor ? 'text-amber-300' : 'text-emerald-300'}`}>
        {field.field_name}
      </span>
      {isSelected && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(field.id);
          }}
          className="ml-auto p-0.5 hover:bg-red-500/20 rounded"
        >
          <Trash2 className="w-3 h-3 text-red-400" />
        </button>
      )}
    </div>
  );
};

export function TemplateFieldEditor({ 
  templateId, 
  fileName,
  existingFields = [],
  onSave,
  onClose,
  authToken
}) {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [fields, setFields] = useState(existingFields);
  const [selectedField, setSelectedField] = useState(null);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [newFieldType, setNewFieldType] = useState('');
  const [newFieldName, setNewFieldName] = useState('');
  const [scale, setScale] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setFields(existingFields);
  }, [existingFields]);

  useEffect(() => {
    loadPreview();
  }, [templateId]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/msa/templates/${templateId}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load document preview');
    } finally {
      setLoading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleContainerClick = (e) => {
    if (e.target === containerRef.current || e.target.classList.contains('react-pdf__Page__canvas')) {
      setSelectedField(null);
    }
  };

  const handleDragStart = (e, field) => {
    e.dataTransfer.setData('fieldId', field.id);
    e.dataTransfer.setData('offsetX', e.nativeEvent.offsetX.toString());
    e.dataTransfer.setData('offsetY', e.nativeEvent.offsetY.toString());
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const fieldId = e.dataTransfer.getData('fieldId');
    const offsetX = parseFloat(e.dataTransfer.getData('offsetX')) || 0;
    const offsetY = parseFloat(e.dataTransfer.getData('offsetY')) || 0;
    
    if (!fieldId) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - offsetX;
    const y = e.clientY - rect.top - offsetY;
    
    setFields(prev => prev.map(f => 
      f.id === fieldId 
        ? { ...f, position_x: Math.max(0, x), position_y: Math.max(0, y), page_number: currentPage }
        : f
    ));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const addField = () => {
    if (!newFieldType) {
      toast.error('Please select a field type');
      return;
    }
    
    const fieldTypeInfo = FIELD_TYPES.find(f => f.value === newFieldType);
    const newField = {
      id: `temp-${Date.now()}`,
      template_id: templateId,
      field_type: newFieldType,
      field_name: newFieldName || fieldTypeInfo?.label || 'New Field',
      role: fieldTypeInfo?.role || 'vendor',
      position_x: 50,
      position_y: 50 + (fields.filter(f => f.page_number === currentPage).length * 40),
      page_number: currentPage,
      width: newFieldType.includes('signature') ? 200 : 180,
      height: newFieldType.includes('signature') ? 50 : 30,
    };
    
    setFields(prev => [...prev, newField]);
    setAddFieldOpen(false);
    setNewFieldType('');
    setNewFieldName('');
    toast.success('Field added. Drag to position.');
  };

  const deleteField = (fieldId) => {
    setFields(prev => prev.filter(f => f.id !== fieldId));
    setSelectedField(null);
    toast.success('Field removed');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(fields);
      toast.success('Template fields saved successfully');
    } catch (error) {
      toast.error('Failed to save fields');
    } finally {
      setSaving(false);
    }
  };

  const currentPageFields = fields.filter(f => f.page_number === currentPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-zinc-950">
        <div className="text-zinc-400">Loading document preview...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-zinc-950 gap-4">
        <AlertCircle className="w-12 h-12 text-red-400" />
        <p className="text-red-400">{error}</p>
        <Button onClick={onClose} variant="outline" className="border-zinc-700">
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-white">Field Mapping Editor</h3>
          <Badge variant="outline" className="border-zinc-700">
            {fileName}
          </Badge>
          {previewData?.type === 'html' && (
            <Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/20">
              Word Document
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setAddFieldOpen(true)}
            size="sm"
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="add-field-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Field
          </Button>
          <Button
            onClick={handleSave}
            size="sm"
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="save-fields-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving...' : 'Save Fields'}
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
            className="border-zinc-700"
          >
            Close
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Field List Sidebar */}
        <div className="w-64 border-r border-zinc-800 p-4 overflow-y-auto">
          <h4 className="text-sm font-medium text-zinc-300 mb-3">Mapped Fields</h4>
          
          <div className="space-y-2">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Vendor Fields</div>
            {fields.filter(f => f.role === 'vendor').map(field => (
              <div
                key={field.id}
                className={`p-2 rounded border cursor-pointer transition-colors ${
                  selectedField?.id === field.id
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-zinc-800 hover:border-amber-500/50'
                }`}
                onClick={() => {
                  setSelectedField(field);
                  setCurrentPage(field.page_number);
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs text-amber-300">{field.field_name}</span>
                  <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400">
                    P{field.page_number}
                  </Badge>
                </div>
              </div>
            ))}
            
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2 mt-4">Company Fields</div>
            {fields.filter(f => f.role === 'company').map(field => (
              <div
                key={field.id}
                className={`p-2 rounded border cursor-pointer transition-colors ${
                  selectedField?.id === field.id
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-zinc-800 hover:border-emerald-500/50'
                }`}
                onClick={() => {
                  setSelectedField(field);
                  setCurrentPage(field.page_number);
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs text-emerald-300">{field.field_name}</span>
                  <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400">
                    P{field.page_number}
                  </Badge>
                </div>
              </div>
            ))}
          </div>

          {fields.length === 0 && (
            <p className="text-xs text-zinc-500 text-center py-4">
              No fields mapped yet. Click "Add Field" to start.
            </p>
          )}
        </div>

        {/* Document Viewer with Fields */}
        <div className="flex-1 overflow-auto bg-zinc-900 p-4">
          {/* Page Navigation for PDF */}
          {previewData?.type === 'pdf' && numPages && (
            <div className="flex items-center justify-center gap-4 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="border-zinc-700"
              >
                Previous
              </Button>
              <span className="text-sm text-zinc-400">
                Page {currentPage} of {numPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                disabled={currentPage === numPages}
                className="border-zinc-700"
              >
                Next
              </Button>
            </div>
          )}

          {/* Document Container */}
          <div
            ref={containerRef}
            className="relative mx-auto border border-zinc-700 rounded-lg overflow-hidden bg-white"
            style={{ width: 'fit-content', minWidth: '612px', minHeight: '792px' }}
            onClick={handleContainerClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            {/* PDF Viewer */}
            {previewData?.type === 'pdf' && (
              <Document
                file={previewData.content}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div className="flex items-center justify-center h-[792px] w-[612px]">
                    <div className="text-zinc-400">Loading PDF...</div>
                  </div>
                }
                error={
                  <div className="flex items-center justify-center h-[792px] w-[612px]">
                    <div className="text-red-400">Failed to load PDF</div>
                  </div>
                }
              >
                <Page
                  pageNumber={currentPage}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            )}

            {/* HTML Viewer (Word documents) */}
            {previewData?.type === 'html' && (
              <div 
                className="p-8 min-h-[792px]"
                style={{ 
                  fontFamily: 'Times New Roman, serif',
                  fontSize: '12pt',
                  lineHeight: '1.5',
                  color: '#000000',
                  backgroundColor: '#ffffff'
                }}
                dangerouslySetInnerHTML={{ __html: previewData.content }}
              />
            )}

            {/* Text Viewer */}
            {previewData?.type === 'text' && (
              <div className="p-8 whitespace-pre-wrap font-mono text-sm min-h-[792px]">
                {previewData.content}
              </div>
            )}

            {/* Unknown type */}
            {previewData?.type === 'unknown' && (
              <div className="flex items-center justify-center h-[792px] w-[612px]">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                  <p className="text-zinc-600">Unsupported file type: {previewData.file_type}</p>
                  <p className="text-zinc-500 text-sm mt-2">Please upload a PDF or Word document</p>
                </div>
              </div>
            )}

            {/* Render Fields */}
            {currentPageFields.map(field => (
              <FieldBadge
                key={field.id}
                field={field}
                isSelected={selectedField?.id === field.id}
                onSelect={setSelectedField}
                onDelete={deleteField}
                onDragStart={handleDragStart}
              />
            ))}
          </div>

          {/* Zoom Controls */}
          {previewData?.type === 'pdf' && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
                className="border-zinc-700"
              >
                -
              </Button>
              <span className="text-sm text-zinc-400 w-16 text-center">
                {Math.round(scale * 100)}%
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScale(s => Math.min(2, s + 0.1))}
                className="border-zinc-700"
              >
                +
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Add Field Dialog */}
      <Dialog open={addFieldOpen} onOpenChange={setAddFieldOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add Field to Template</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Select a field type to add to the document
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Field Type</Label>
              <Select value={newFieldType} onValueChange={setNewFieldType}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800">
                  <SelectValue placeholder="Select field type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <div className="px-2 py-1 text-xs text-zinc-500 uppercase">Vendor Fields</div>
                  {FIELD_TYPES.filter(f => f.role === 'vendor').map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4 text-amber-400" />
                        <span>{type.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs text-zinc-500 uppercase mt-2">Company Fields</div>
                  {FIELD_TYPES.filter(f => f.role === 'company').map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4 text-emerald-400" />
                        <span>{type.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Custom Label (Optional)</Label>
              <Input
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                placeholder="Enter custom label"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setAddFieldOpen(false)}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={addField}
                className="bg-blue-600 hover:bg-blue-500"
              >
                Add Field
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
